'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';
import { Icon, IconName } from '~/components/core/Icon';
import { getAppCheckToken } from '~/lib/firebase-app-check';

export type Address = {
  street: string;
  city: string;
  postal_code: string;
  state: string | null;
  state_code: string | null;
  country: string | null;
  country_code: string | null;
};

export type AddressFieldProps = {
  value?: Address; // pre-populate field if backend provides
  onChange?: (address: Address) => void;
  isDisabled?: boolean;
  placeholder?: string;
  error?: boolean; // Whether to show error styling
};

/**
 * AddressField component with Google Places autocomplete
 */
// Type for the new Places API (New) response
type AutocompleteSuggestion = {
  placePrediction: {
    placeId: string;
    text: {
      text: string;
      matches: Array<{ startOffset: number; endOffset: number }>;
    };
    structuredFormat: {
      mainText: { text: string; matches: Array<{ startOffset: number; endOffset: number }> };
      secondaryText: { text: string };
    };
    types: string[];
  };
};

const AddressFieldAutoComplete = ({
  value,
  onChange,
  isDisabled,
  placeholder,
  error,
}: AddressFieldProps) => {
  // Construct display value from address fields
  const getDisplayValue = useCallback((addr?: Address): string => {
    if (!addr) return '';
    const parts: string[] = [];
    if (addr.street) parts.push(addr.street);
    if (addr.postal_code && addr.city) {
      parts.push(`${addr.postal_code} ${addr.city}`);
    } else if (addr.city) {
      parts.push(addr.city);
    } else if (addr.postal_code) {
      parts.push(addr.postal_code);
    }
    if (addr.state) parts.push(addr.state);
    if (addr.country) parts.push(addr.country);
    return parts.join(', ');
  }, []);

  const [inputValue, setInputValue] = useState(getDisplayValue(value));
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const sessionTokenRef = useRef<string | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Calculate dropdown position when suggestions change
  useEffect(() => {
    if (suggestions.length > 0 && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    } else {
      setDropdownPosition(null);
    }
  }, [suggestions.length]);

  // Generate a new session token for each autocomplete session
  const generateSessionToken = useCallback(() => {
    return (
      Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
    );
  }, []);

  // Update inputValue if value prop changes
  useEffect(() => {
    if (value) {
      const displayValue = getDisplayValue(value);
      if (displayValue) {
        setInputValue(displayValue);
      }
    }
  }, [value, getDisplayValue]);

  const fetchSuggestions = useCallback(
    async (query: string) => {
      if (query.length < 3) {
        setSuggestions([]);
        return;
      }

      // Check if API key is configured
      const apiKey = process.env.NEXT_PUBLIC_GOOGLE_API_KEY;
      if (!apiKey) {
        console.warn(
          'Google Places API key is not configured. Please set NEXT_PUBLIC_GOOGLE_API_KEY environment variable.',
        );
        setSuggestions([]);
        return;
      }

      // Generate new session token if not exists
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = generateSessionToken();
      }

      setLoading(true);
      try {
        const appCheckToken = await getAppCheckToken();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': apiKey,
          'X-Goog-FieldMask':
            'suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat',
          'Accept-Language': 'en-US',
        };
        if (appCheckToken) {
          headers['X-Firebase-AppCheck'] = appCheckToken;
        }

        const response = await fetch(`https://places.googleapis.com/v1/places:autocomplete`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            input: query,
            sessionToken: sessionTokenRef.current,
            includedRegionCodes: [], // Empty array means all regions
          }),
        });

        if (!response.ok) {
          // Try to get more detailed error information
          let errorMessage = `Places API error: ${response.status} ${response.statusText}`;
          try {
            const errorData = await response.json();
            if (errorData.error?.message) {
              errorMessage = `Places API error: ${errorData.error.message}`;
            }
          } catch {
            // If we can't parse the error response, use the default message
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();
        if (data.suggestions && data.suggestions.length > 0) {
          setSuggestions(data.suggestions);
        } else {
          setSuggestions([]);
        }
      } catch (error) {
        // Only log errors that aren't expected (like network issues)
        if (error instanceof Error) {
          // Don't log if it's a missing API key (already handled above)
          if (!error.message.includes('API key')) {
            console.error('Failed to fetch autocomplete suggestions:', error.message);
          }
        } else {
          console.error('Failed to fetch autocomplete suggestions:', error);
        }
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    },
    [generateSessionToken],
  );

  const handleSelectSuggestion = useCallback(
    async (suggestion: AutocompleteSuggestion) => {
      setSuggestions([]);
      const placeId = suggestion.placePrediction.placeId;
      const displayText = suggestion.placePrediction.text.text;
      setInputValue(displayText);

      // Check if API key is configured
      const apiKey = process.env.NEXT_PUBLIC_GOOGLE_API_KEY;
      if (!apiKey) {
        console.warn(
          'Google Places API key is not configured. Please set NEXT_PUBLIC_GOOGLE_API_KEY environment variable.',
        );
        return;
      }

      try {
        // Use the new Places API (New) for place details
        const appCheckToken = await getAppCheckToken();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': apiKey,
          'X-Goog-FieldMask': 'id,displayName,formattedAddress,location,addressComponents,plusCode',
          'Accept-Language': 'en-US',
        };
        if (appCheckToken) {
          headers['X-Firebase-AppCheck'] = appCheckToken;
        }

        const response = await fetch(`https://places.googleapis.com/v1/places/${placeId}`, {
          method: 'GET',
          headers,
        });

        if (!response.ok) {
          // Try to get more detailed error information
          let errorMessage = `Places API error: ${response.status} ${response.statusText}`;
          try {
            const errorData = await response.json();
            if (errorData.error?.message) {
              errorMessage = `Places API error: ${errorData.error.message}`;
            }
          } catch {
            // If we can't parse the error response, use the default message
          }
          throw new Error(errorMessage);
        }

        const place = await response.json();
        const newAddress: Address = {
          street: '',
          city: '',
          postal_code: '',
          state: null,
          state_code: null,
          country: null,
          country_code: null,
        };

        // Extract address components
        type AddressComponent = {
          longText: string;
          shortText: string;
          types: string[];
        };
        place.addressComponents?.forEach((component: AddressComponent) => {
          const types = component.types || [];
          if (types.includes('street_number')) {
            newAddress.street =
              (newAddress.street ? newAddress.street + ' ' : '') + component.longText;
          }
          if (types.includes('route')) {
            newAddress.street =
              (newAddress.street ? newAddress.street + ' ' : '') + component.longText;
          }
          if (types.includes('locality')) {
            newAddress.city = component.longText;
          }
          if (types.includes('postal_code')) {
            newAddress.postal_code = component.longText;
          }
          if (types.includes('administrative_area_level_1')) {
            newAddress.state = component.longText;
            newAddress.state_code = component.shortText;
          }
          if (types.includes('country')) {
            newAddress.country = component.longText;
            newAddress.country_code = component.shortText;
          }
        });

        // Fallback to formatted address if street is empty (use display text)
        if (!newAddress.street && displayText) {
          newAddress.street = displayText;
        }

        // Only call onChange if we have at least city or postal_code
        if (newAddress.city || newAddress.postal_code) {
          onChange?.(newAddress);
        }

        // Consume the session token after place selection
        if (sessionTokenRef.current) {
          // Optionally, you can make a request to consume the token for billing optimization
          sessionTokenRef.current = null; // Reset for next session
        }
      } catch (error) {
        // Only log errors that aren't expected (like network issues)
        if (error instanceof Error) {
          // Don't log if it's a missing API key (already handled above)
          if (!error.message.includes('API key')) {
            console.error('Failed to fetch place details:', error.message);
          }
        } else {
          console.error('Failed to fetch place details:', error);
        }
      }
    },
    [onChange],
  );

  return (
    <>
      <div className="relative w-full" ref={mapRef}>
        <input
          ref={inputRef}
          type="text"
          className={twMerge(
            'w-full border border-gray-300 rounded-md pr-3 pl-6 h-10 text-sm focus:outline-none focus:ring-0',
            isDisabled &&
              'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-50 cursor-not-allowed',
            error && 'border-red-500',
          )}
          placeholder={placeholder || 'Enter address'}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            fetchSuggestions(e.target.value);
          }}
          disabled={isDisabled}
        />

        {loading && (
          <div className="absolute right-2 top-2">
            <Icon name={IconName.Loader} className="animate-spin text-gray-500" />
          </div>
        )}
      </div>

      {/* Suggestions Dropdown - Rendered via portal to escape overflow containers */}
      {suggestions.length > 0 &&
        dropdownPosition &&
        typeof window !== 'undefined' &&
        createPortal(
          <ul
            className="fixed z-[9999] bg-white shadow-lg border border-gray-200 rounded-md max-h-60 overflow-y-auto"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            {suggestions.map((suggestion, index) => {
              const prediction = suggestion.placePrediction;
              const mainText = prediction.structuredFormat?.mainText?.text || prediction.text.text;
              const secondaryText = prediction.structuredFormat?.secondaryText?.text || '';
              return (
                <li
                  key={prediction.placeId || index}
                  className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                  onClick={() => handleSelectSuggestion(suggestion)}
                >
                  <div className="font-medium">{mainText}</div>
                  {secondaryText && <div className="text-xs text-gray-500">{secondaryText}</div>}
                </li>
              );
            })}
          </ul>,
          document.body,
        )}
    </>
  );
};

export default AddressFieldAutoComplete;
